import { z } from 'zod';
import { BaggageAllowanceSchema } from '../../flight/baggage_allowance.js';
import {
  ModalSessionFlightPolicyViolationSchema,
  ModalSessionFlightPolicyViolationsWireSchema,
} from './flight_policy_violation.js';
import { ModalSessionMoneySchema } from './session';

/*
 * O tariff-view de voo: as famílias tarifárias de uma oferta como a escolha de
 * tarifa as lê — o contrato estrito do produtor (modal-session) e o fio tolerante
 * dos consumidores.
 */

const ModalSessionFlightFareBenefitViewSchema = z
  .object({
    text: z.string().min(1),
    status: z.enum(['ok', 'violated', 'warn', 'na']),
  })
  .strict();

/** Estado de UMA dimensão da tarifa, declarado pelo fornecedor (`details[]`) ou
 *  pelo mapa curado da VOLL. `unknown` é "ninguém declarou": o card volta ao
 *  texto do fornecedor. */
export const ModalSessionFlightFareAttributeStateSchema = z.enum([
  'included',
  'fee',
  'not_offered',
  'unknown',
]);
export type ModalSessionFlightFareAttributeState = z.infer<
  typeof ModalSessionFlightFareAttributeStateSchema
>;

/** Atributos estruturados da família tarifária, uma linha por dimensão do card.
 *  `source` é QUEM declarou: `wooba` = as características tipadas do fornecedor
 *  (`details[]` do tariff, ver voll-apollo); `voll` = o mapa curado da VOLL por
 *  companhia (`flightCompanyFareCache` do gateway legado) casou a família — as
 *  linhas de bagagem e reembolso do `details[]` já vêm dele, e seus campos tipados
 *  completam assento, troca e reembolso. `confidence` é a constante `high`: o
 *  produtor só anexa atributos que o fornecedor ou o mapa curado afirmaram.
 *  Opcional na tarifa: sem declaração, não existe. */
export const ModalSessionFlightFareAttributesSchema = z
  .object({
    source: z.enum(['wooba', 'voll']),
    confidence: z.literal('high'),
    carry: ModalSessionFlightFareAttributeStateSchema,
    checked: ModalSessionFlightFareAttributeStateSchema,
    seat: ModalSessionFlightFareAttributeStateSchema,
    change: ModalSessionFlightFareAttributeStateSchema,
    refund: ModalSessionFlightFareAttributeStateSchema,
  })
  .strict();
export type ModalSessionFlightFareAttributes = z.infer<
  typeof ModalSessionFlightFareAttributesSchema
>;

const ModalSessionFlightFareViewSchema = z
  .object({
    fareId: z.string().min(1),
    name: z.string().min(1),
    price: ModalSessionMoneySchema,
    withinPolicy: z.boolean().nullish(),
    /** Por que a família está fora (#5797): os mesmos motivos que o checkout
     *  nomeia ao exigir justificativa. Só com `withinPolicy: false`. */
    policyViolations: z.array(ModalSessionFlightPolicyViolationSchema).optional(),
    benefits: z.array(ModalSessionFlightFareBenefitViewSchema).optional(),
    baggage: z.string().optional(),
    attributes: ModalSessionFlightFareAttributesSchema.optional(),
    /** A franquia que a tarifa declara (peças e quilos da mão e do despacho, #6542) — a
     *  mesma forma da do voucher emitido. Só existe quando há número: sem ele, o card
     *  fica na frase da dimensão. */
    baggageAllowance: BaggageAllowanceSchema.optional(),
  })
  .strict();
export type ModalSessionFlightFareView = z.infer<typeof ModalSessionFlightFareViewSchema>;

const ModalSessionFlightTariffBundleViewSchema = z
  .object({
    fareId: z.string().min(1),
    departing: ModalSessionFlightFareViewSchema,
    returning: ModalSessionFlightFareViewSchema,
  })
  .strict();

export const ModalSessionFlightTariffViewSchema = z
  .object({
    departing: z.array(ModalSessionFlightFareViewSchema),
    returning: z.array(ModalSessionFlightFareViewSchema),
    bundled: z.array(ModalSessionFlightTariffBundleViewSchema),
  })
  .strict();
export type ModalSessionFlightTariffView = z.infer<typeof ModalSessionFlightTariffViewSchema>;

/**
 * FIO TOLERANTE do tariff-view — para CONSUMIDORES (app-integration, web-chat).
 * O produtor (modal-session) valida o que emite com o `.strict()` acima; quem lê
 * precisa sobreviver a um produtor mais novo na janela de deploy (os três jobs
 * do deploy rodam em paralelo e o web-chat é SPA com assets imutáveis): campo
 * desconhecido é DESCARTADO em todo nível, e um `status` de benefício ausente
 * OU que esta versão não conhece vira `na` (não avaliado) em vez de derrubar a
 * escada — o `.catch('na')` cobre as duas faltas, não só o valor desconhecido.
 * Campo obrigatório ausente ou de tipo errado (fora do `status`) ainda é
 * REJEITADO: a tolerância é só para chaves aditivas, não para o contrato base.
 * Mesma razão do `strip()` do legs-view (d23796f6f) e do PageInfo (3e998c6aa).
 */
const ModalSessionFlightFareBenefitWireSchema = ModalSessionFlightFareBenefitViewSchema.extend({
  status: ModalSessionFlightFareBenefitViewSchema.shape.status.catch('na'),
}).strip();
/** Gêmeo tolerante dos atributos: chave nova DENTRO do objeto é descartada,
 *  estado que esta versão não conhece vira `unknown` (o card volta ao texto),
 *  e um objeto malformado derruba só os atributos — nunca a tarifa. Chave
 *  obrigatória AUSENTE dentro deles (`confidence`, ou uma das cinco dimensões)
 *  ainda derruba os atributos — a tolerância é só para o VALOR desconhecido de
 *  um estado, não para a falta da chave (`source` e `confidence` não têm
 *  fallback: valor que esta versão não conhece derruba os atributos). Por isso
 *  o `.catch()` não vai direto no enum:
 *  `z.enum(...).catch(fallback)` marca o campo opcional para o objeto pai (o
 *  Zod 4.4.3 restaura esse comportamento), então uma chave ausente também
 *  coagia para o fallback em vez de reprovar. `z.string().pipe(enum.catch(...))`
 *  exige um STRING presente antes de tentar coagir: ausência (ou tipo errado)
 *  falha no `z.string()`, e só um valor suprido — string desconhecida incluída
 *  — chega ao `.catch()`. A falha do objeto sobe até o `.optional().catch(undefined)`
 *  do fio abaixo, que descarta só os atributos (contrato base), nunca a tarifa. */
const attributeStateWire = z
  .string()
  .pipe(ModalSessionFlightFareAttributeStateSchema.catch('unknown'));
const ModalSessionFlightFareAttributesWireSchema = ModalSessionFlightFareAttributesSchema.extend({
  carry: attributeStateWire,
  checked: attributeStateWire,
  seat: attributeStateWire,
  change: attributeStateWire,
  refund: attributeStateWire,
}).strip();
const ModalSessionFlightFareViewWireSchema = ModalSessionFlightFareViewSchema.extend({
  price: ModalSessionMoneySchema.strip(),
  benefits: z.array(ModalSessionFlightFareBenefitWireSchema).optional(),
  attributes: ModalSessionFlightFareAttributesWireSchema.optional().catch(undefined),
  // Franquia malformada derruba só ela — a tarifa segue, com as linhas sem número.
  baggageAllowance: BaggageAllowanceSchema.optional().catch(undefined),
  policyViolations: ModalSessionFlightPolicyViolationsWireSchema,
}).strip();
export const ModalSessionFlightTariffViewWireSchema = ModalSessionFlightTariffViewSchema.extend({
  departing: z.array(ModalSessionFlightFareViewWireSchema),
  returning: z.array(ModalSessionFlightFareViewWireSchema),
  bundled: z.array(
    ModalSessionFlightTariffBundleViewSchema.extend({
      departing: ModalSessionFlightFareViewWireSchema,
      returning: ModalSessionFlightFareViewWireSchema,
    }).strip(),
  ),
}).strip();
